import { hash } from 'ohash'
import moment from 'moment'
import type { OriginProductItem, SearchForm } from './types.d'

export type CacheValue = {
  /**
   * hash(SearchForm)
   */
  key: string
  /**
   * 原始表单数据 JSON.stringify
   */
  rawQuery: string
  /**
   * 是否还有更多的数据
   */
  hasMore: boolean
  /**
   * 最后缓存的page
   */
  lastPage: number
  /**
   * 最后缓存更新的时间
   */
  lastCacheTime: string
  /**
   * 缓存数据
   * K: 页码
   * V: 缓存的数据
   */
  data: Map<number, Array<OriginProductItem>>
}

export type ProductCache = Map<string, CacheValue>

type GetCachedProductItemResult = {
  data: Array<OriginProductItem>
  hasMore: boolean
  expired: boolean
} | null

/**
 * 针对每个表单项加上page生成缓存
 */
class ProductListCacheManager {
  private _cache: ProductCache

  constructor() {
    this._cache = new Map()
  }

  /**
   * 获取指定查询条件下的商品数据，没有缓存的数据返回null
   * @param form 查询条件
   * @param page 页
   */
  public getCache(form: SearchForm, page: number): GetCachedProductItemResult {
    const now = Date.now()
    const cacheKey = hash(form)

    const cacheData = this._cache.get(cacheKey)

    if (!cacheData) {
      return null
    }

    const pageCache = cacheData.data.get(page)

    if (!pageCache) return null

    // 检查缓存是否失效 数据缓存5分钟
    const lastCacheTimestemp = moment(cacheData.lastCacheTime).valueOf()
    const cachedMinutes = moment.duration(now - lastCacheTimestemp).asMinutes()

    return {
      data: pageCache,
      hasMore: cacheData.hasMore || page < cacheData.lastPage,
      expired: cachedMinutes >= 5
    }
  }

  /**
   * 接口请求成功后设置本地缓存
   * @param list 返回的列表数据
   * @param rule 一些源数据
   */
  public setCache(list: Array<OriginProductItem>, rule: { form: SearchForm; page: number; hasMore: boolean }) {
    const cacehKey = hash(rule.form)
    const cacheData = this._cache.get(cacehKey)

    const _data = cacheData ? cacheData.data : new Map<number, Array<OriginProductItem>>()
    _data.set(rule.page, list)

    const cacheValue = {
      key: cacehKey,
      rawQuery: JSON.stringify(rule.form),
      lastPage: rule.page,
      lastCacheTime: moment().format(),
      hasMore: rule.hasMore,
      data: _data
    } satisfies CacheValue

    this._cache.set(cacehKey, cacheValue)
  }

  /**
   * 删除指定查询条件的缓存，缓存过期了需要删除
   * @param form 查询条件
   */
  public removeItem(form: SearchForm) {
    const cacheKey = hash(form)
    this._cache.delete(cacheKey)
  }

  public clear() {
    this._cache.clear()
  }
}

export const cacheManager = new ProductListCacheManager()

